// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getAssets } from '@/asset/api/get-assets';
import { Asset } from '@/asset/api/types';
import { navigationSectionsConfig } from '@/constants/navigation-sections';
import {
  getCustomers,
  isSearchableCustomerQuery,
} from '@/customer/api/get-customers';
import { Customer } from '@/customer/api/types';
import { getFulfillments } from '@/fulfillment/api/get-fulfillments';
import { FulfillmentOrder } from '@/fulfillment/api/types';
import { getLocations } from '@/location/api/get-locations';
import { Location } from '@/location/api/types';
import { getProjects } from '@/project/api/get-projects';
import { Project } from '@/project/api/types';
import { getPurchaseOrders } from '@/purchase-order/api/get-purchase-orders';
import { PurchaseOrder } from '@/purchase-order/api/types';
import { getReturnOrders } from '@/return-order/api/get-return-orders';
import { ReturnOrder } from '@/return-order/api/types';
import { getShipments } from '@/shipment/api/get-shipments';
import { Shipment } from '@/shipment/api/types';

interface GlobalSearchParams {
  query: string;
}

/**
 * Navigation item result from search
 */
export interface NavigationSearchResult {
  label: string;
  description: string;
  to: string;
  icon: React.ReactElement;
  sectionLabel: string;
  disabled?: boolean;
}

export interface GlobalSearchResult {
  assets: Asset[];
  locations: Location[];
  customers: Customer[];
  projects: Project[];
  purchaseOrders: PurchaseOrder[];
  fulfillmentOrders: FulfillmentOrder[];
  returnOrders: ReturnOrder[];
  inboundShipments: Shipment[];
  outboundShipments: Shipment[];
  navigation: NavigationSearchResult[];
}

/**
 * Searches navigation items based on query
 */
function searchNavigationItems(query: string): NavigationSearchResult[] {
  const lowerQuery = query.toLowerCase();
  const results: NavigationSearchResult[] = [];

  for (const section of navigationSectionsConfig) {
    for (const item of section.items) {
      if (item.disabled) {
        continue;
      }

      const labelMatch = item.label.toLowerCase().includes(lowerQuery);
      const descriptionMatch = item.description
        .toLowerCase()
        .includes(lowerQuery);
      const sectionMatch = section.label.toLowerCase().includes(lowerQuery);

      if (labelMatch || descriptionMatch || sectionMatch) {
        results.push({
          label: item.label,
          description: item.description,
          to: item.to,
          icon: item.icon,
          sectionLabel: section.label,
        });
      }
    }
  }

  return results;
}

export async function globalSearch(
  params: GlobalSearchParams
): Promise<GlobalSearchResult> {
  const [
    assetsResponse,
    locationsResponse,
    customersResponse,
    projectsResponse,
    purchaseOrdersResponse,
    fulfillmentsResponse,
    returnOrdersResponse,
    inboundShipmentsResponse,
    outboundShipmentsResponse,
  ] = await Promise.all([
    getAssets({ query: params.query, pageSize: 5 }),
    getLocations({ query: params.query }),
    isSearchableCustomerQuery(params.query)
      ? getCustomers({ query: params.query })
      : Promise.resolve({ customers: [] }),
    getProjects({ query: params.query, pageSize: 5 }),
    getPurchaseOrders({ searchTerm: params.query, pageSize: 5 }),
    getFulfillments({ searchTerm: params.query, pageSize: 5 }),
    getReturnOrders({ searchTerm: params.query, pageSize: 5 }),
    getShipments({ query: params.query, shipmentType: 'inbound', pageSize: 5 }),
    getShipments({
      query: params.query,
      shipmentType: 'outbound',
      pageSize: 5,
    }),
  ]);

  const navigationResults = searchNavigationItems(params.query);

  return {
    assets: assetsResponse.assets,
    locations: locationsResponse.locations,
    customers: customersResponse.customers,
    projects: projectsResponse.projects,
    purchaseOrders: purchaseOrdersResponse.purchaseOrders,
    fulfillmentOrders: fulfillmentsResponse.fulfillments,
    returnOrders: returnOrdersResponse.returnOrders,
    inboundShipments: inboundShipmentsResponse.shipments,
    outboundShipments: outboundShipmentsResponse.shipments,
    navigation: navigationResults,
  };
}
